// Shared header used across all authenticated pages.
//
// Layout A — no projectName prop (Home, Admin, Single):
//   [logo] [title + subtitle]  ←spacer→  [⌘K, ThemeToggle, Help, Admin, SignOut]
//
// Layout B — projectName present (inside Project Wizard):
//   [logo] [projectName (prominent, truncate)]  ←spacer→  [title · subtitle (quiet)] [actions]
//
// Tour button removed from the header — accessible via Help → "Start guided tour".
// ⌘K is a visual affordance only; opens a "coming soon" toast until the palette lands.

import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "./ui/dropdown-menu";
import { HelpCircle, MessageSquare, ShieldCheck, LogOut, BookOpen, Mail, Home } from "lucide-react";
import { useAuth } from "../lib/auth";
import FeedbackDialog from "./FeedbackDialog";
import OnboardingTour from "./OnboardingTour";
import ThemeToggle from "./ThemeToggle";
import { toast } from "sonner";

export default function AppHeader({ title, subtitle, projectName, rightExtras }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [tourOpen, setTourOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const showHome = !!user && location.pathname !== "/home";

  const handleCmdK = () => {
    toast.info("Command palette coming soon");
  };

  return (
    <>
      <div className="flex items-center gap-3 mb-6" data-testid="app-header">
        {/* Logo — always leftmost */}
        <Link to={user ? "/home" : "/"} className="flex items-center shrink-0">
          <img
            src="/zamba-logo.png"
            alt="ZAMBA"
            className="h-7 w-auto"
            data-testid="header-logo"
          />
        </Link>

        {/* Layout B: project name is the dominant left element */}
        {projectName ? (
          <>
            <div
              className="text-base font-semibold truncate min-w-0 flex-1"
              data-testid="header-project-name"
              title={projectName}
            >
              {projectName}
            </div>

            {/* Title + subtitle cluster — visually quieter, right-side, hidden on narrow screens */}
            {title && (
              <div className="hidden md:flex flex-col items-end shrink-0 mr-1">
                <span className="text-sm text-muted-foreground font-medium leading-tight">{title}</span>
                {subtitle && (
                  <span className="text-xs text-muted-foreground/70 leading-tight">{subtitle}</span>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            {/* Layout A: title sits next to logo on the left */}
            {title && (
              <div className="min-w-0 flex-1">
                <h1 className="text-base font-semibold tracking-tight truncate">{title}</h1>
                {subtitle && (
                  <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
                )}
              </div>
            )}
            {/* Spacer when no title either */}
            {!title && <div className="flex-1" />}
          </>
        )}

        {/* Right: actions */}
        <div className="flex items-center gap-1 shrink-0">
          {/* ⌘K affordance — visual placeholder, real implementation is a later phase */}
          <button
            onClick={handleCmdK}
            className="hidden sm:flex items-center gap-1.5 px-2 py-1 rounded border border-border text-xs text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors font-mono"
            title="Command palette"
            data-testid="cmdK-trigger"
          >
            <span>⌘K</span>
          </button>

          <ThemeToggle />

          {rightExtras}

          {showHome && (
            <Link to="/home">
              <Button
                variant="ghost"
                size="sm"
                data-testid="header-home"
                title="Return to the home page"
              >
                <Home className="h-4 w-4 mr-1" /> Home
              </Button>
            </Link>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" data-testid="header-help">
                <HelpCircle className="h-4 w-4 mr-1" /> Help
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel>Need a hand?</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <a
                  href="mailto:support@zambahvac.com?subject=ZAMBA%20Selection%20Tool%20-%20Support"
                  data-testid="header-help-email"
                  className="flex items-center cursor-pointer"
                >
                  <Mail className="h-4 w-4 mr-2 text-muted-foreground" />
                  <div>
                    <div className="text-sm font-medium">Email Support</div>
                    <div className="text-xs text-muted-foreground">support@zambahvac.com</div>
                  </div>
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTourOpen(true)} className="cursor-pointer">
                <BookOpen className="h-4 w-4 mr-2 text-muted-foreground" />
                <div>
                  <div className="text-sm font-medium">Start guided tour</div>
                  <div className="text-xs text-muted-foreground">Walk through the workflows</div>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFeedbackOpen(true)} className="cursor-pointer">
                <MessageSquare className="h-4 w-4 mr-2 text-muted-foreground" />
                <div>
                  <div className="text-sm font-medium">Send feedback</div>
                  <div className="text-xs text-muted-foreground">Suggestions or bugs</div>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {user?.role === "admin" && (
            <Link to="/admin">
              <Button variant="ghost" size="sm" data-testid="header-admin">
                <ShieldCheck className="h-4 w-4 mr-1" /> Admin
              </Button>
            </Link>
          )}
          {user && (
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              data-testid="header-signout"
            >
              <LogOut className="h-4 w-4 mr-1" /> Sign out
            </Button>
          )}
        </div>
      </div>

      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
      <OnboardingTour open={tourOpen} onOpenChange={setTourOpen} />
    </>
  );
}
